export const ANALYTICS_ENDPOINT = "https://mutinyapp.gg/analytics/api/event";
const ANALYTICS_ORIGIN = "https://app.mutinyapp.gg";
const EVENTS = new Set(["pageview", "mutiny_registration_start"]);
const STATIC_ROUTES = new Set([
  "/",
  "/app",
  "/login",
  "/login/check",
  "/login/create",
  "/login/auth",
  "/login/resend",
  "/login/reset",
  "/patch-notes",
  "/pwa",
  "/dev",
  "/settings",
  "/friends",
  "/discover",
]);
const DYNAMIC_ROUTES = [
  [/^\/server\/[^/]+\/channel\/[^/]+$/, "/server/:server/channel/:channel"],
  [/^\/server\/[^/]+$/, "/server/:server"],
  [/^\/channel\/[^/]+$/, "/channel/:channel"],
  [/^\/invite\/[^/]+$/, "/invite/:code"],
  [/^\/bot\/[^/]+$/, "/bot/:code"],
  [/^\/login\/create\/[^/]+$/, "/login/create/:code"],
  [/^\/login\/verify\/[^/]+$/, "/login/verify/:token"],
  [/^\/login\/reset\/[^/]+$/, "/login/reset/:token"],
  [/^\/login\/delete\/[^/]+$/, "/login/delete/:token"],
];

export function routeTemplate(pathname, baseUrl = "/") {
  if (typeof pathname !== "string" || typeof baseUrl !== "string")
    return "/unknown";
  const base = baseUrl.endsWith("/") ? baseUrl : `${baseUrl}/`;
  if (!base.startsWith("/") || !pathname.startsWith(base)) return "/unknown";
  const path = `/${pathname.slice(base.length)}`;
  if (path === "/dev" || path.startsWith("/dev/")) return null;
  if (STATIC_ROUTES.has(path)) return path;
  return (
    DYNAMIC_ROUTES.find(([pattern]) => pattern.test(path))?.[1] || "/unknown"
  );
}

export function collectionDisabled(windowObject) {
  const navigator = windowObject.navigator || {};
  return (
    navigator.globalPrivacyControl === true ||
    [
      navigator.doNotTrack,
      navigator.msDoNotTrack,
      windowObject.doNotTrack,
    ].some((value) => value === "1" || value === "yes" || value === 1)
  );
}

// Own the complete wire payload: the upstream auto-tracker reads location.href
// and document.referrer regardless of registration options. Never delegate to it.
export function installAnalyticsSender({ windowObject, baseUrl }) {
  const queued = windowObject.plausible?.q || [];
  windowObject.plausible = function plausible(name) {
    try {
      if (!EVENTS.has(name) || collectionDisabled(windowObject)) return;
      const template = routeTemplate(windowObject.location.pathname, baseUrl);
      if (template === null) return;
      const payload = {
        n: name,
        d: "app.mutinyapp.gg",
        u: `${ANALYTICS_ORIGIN}${routeTemplate(windowObject.location.pathname, baseUrl)}`,
      };
      Promise.resolve(
        windowObject.fetch(ANALYTICS_ENDPOINT, {
          method: "POST",
          headers: { "Content-Type": "text/plain" },
          credentials: "omit",
          referrerPolicy: "no-referrer",
          keepalive: true,
          body: JSON.stringify(payload),
        }),
      ).catch(() => {});
    } catch {
      // Collection failure must never interrupt navigation or authentication.
    }
  };
  for (const args of queued) windowObject.plausible(args[0]);
  // Compare actual pathname in memory, never export it. Query/hash-only changes
  // do not count; distinct IDs count as navigation but share a bounded template.
  if (windowObject.history && windowObject.addEventListener) {
    let previousPath;
    const pageview = () => {
      const pathname = windowObject.location.pathname;
      if (pathname === previousPath) return;
      previousPath = pathname;
      windowObject.plausible("pageview");
    };
    for (const method of ["pushState", "replaceState"]) {
      const original = windowObject.history[method];
      windowObject.history[method] = function (...args) {
        const result = original.apply(this, args);
        pageview();
        return result;
      };
    }
    windowObject.addEventListener("popstate", pageview);
    pageview();
  }
}

export function registrationPath({ origin, baseUrl }) {
  const normalizedOrigin = new globalThis.URL(origin).origin;
  const normalizedBaseUrl = baseUrl.endsWith("/") ? baseUrl : `${baseUrl}/`;
  return new globalThis.URL(
    "login/create",
    new globalThis.URL(normalizedBaseUrl, `${normalizedOrigin}/`),
  ).pathname;
}

export function isRegistrationTarget(target, { origin, baseUrl }) {
  try {
    const anchor = target?.closest?.("a[href]");
    if (!anchor) return false;
    const normalizedOrigin = new globalThis.URL(origin).origin;
    const destination = new globalThis.URL(anchor.href, `${normalizedOrigin}/`);
    return (
      destination.origin === normalizedOrigin &&
      destination.pathname === registrationPath({ origin, baseUrl })
    );
  } catch {
    return false;
  }
}

export function installRegistrationTracking({
  windowObject,
  documentObject,
  baseUrl,
}) {
  windowObject.plausible =
    windowObject.plausible ||
    function plausible() {
      (windowObject.plausible.q = windowObject.plausible.q || []).push(
        arguments,
      );
    };
  documentObject.addEventListener(
    "click",
    function trackRegistrationStart(event) {
      try {
        if (
          !collectionDisabled(windowObject) &&
          routeTemplate(windowObject.location.pathname, baseUrl) !== null &&
          isRegistrationTarget(event.target, {
            origin: windowObject.location.origin,
            baseUrl,
          })
        ) {
          // Intent only: never an account-created or activation completion.
          windowObject.plausible("mutiny_registration_start", {
            u: `${ANALYTICS_ORIGIN}${routeTemplate(windowObject.location.pathname, baseUrl)}`,
          });
        }
      } catch {
        // Analytics must not block the link's ordinary behavior.
      }
    },
    true,
  );
}

if (
  typeof globalThis.window !== "undefined" &&
  typeof globalThis.document !== "undefined"
) {
  const options = {
    windowObject: globalThis.window,
    documentObject: globalThis.document,
    baseUrl: import.meta.env.BASE_URL,
  };
  installAnalyticsSender(options);
  installRegistrationTracking(options);
}
